import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Circle, Loader2, Mic, Pause, Play, Square, TriangleAlert } from 'lucide-react';

interface Props {
  /** Appelé quand l'enregistrement est arrêté et prêt à être déposé. */
  onEnregistre: (fichier: File) => void;
  desactive?: boolean;
}

/**
 * Enregistrement d'une réunion depuis la page, sans passer par un fichier.
 *
 * Demander à un agent d'enregistrer sur son téléphone, de retrouver le fichier
 * puis de le téléverser ajoute deux occasions de perdre la réunion. Ici la
 * captation et le dépôt sont le même geste.
 *
 * Le niveau sonore est affiché pendant toute la captation. Ce n'est pas un
 * ornement : la panne la plus coûteuse est d'enregistrer une heure sur un
 * micro muet, et on ne s'en aperçoit qu'à la fin. Une barre qui bouge est la
 * seule preuve que la salle est bien captée.
 */

/** Formats tentés dans l'ordre : Opus d'abord, très léger sur la parole. */
const FORMATS = [
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/webm',
  'audio/mp4',
];

/** Trente-deux kilobits suffisent pour de la parole, et allègent d'autant. */
const DEBIT = 32000;

/** En deçà, la captation n'a rien retenu : à ce débit, une seconde pèse plus. */
const TAILLE_MIN = 2 * 1024;

const EXTENSIONS: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/mp4': 'm4a',
};

/**
 * Pourquoi le micro ne s'ouvre pas, dit précisément.
 *
 * Tout était rangé sous « aucun micro n'a été trouvé », y compris le cas le
 * plus fréquent : un micro bien présent mais retenu par une autre application.
 * Un agent dont la visioconférence tourne encore lisait qu'il n'avait pas de
 * micro, et cherchait du matériel au lieu de fermer une fenêtre.
 */
const MOTIFS: Record<string, string> = {
  NotAllowedError:
    "L'accès au micro a été refusé. Autorisez-le dans le navigateur, à gauche "
    + "de l'adresse du site, puis réessayez.",
  PermissionDeniedError:
    "L'accès au micro a été refusé. Autorisez-le dans le navigateur, à gauche "
    + "de l'adresse du site, puis réessayez.",
  NotFoundError:
    "Aucun micro n'a été trouvé. Vérifiez qu'il est branché et reconnu par "
    + "l'appareil.",
  DevicesNotFoundError:
    "Aucun micro n'a été trouvé. Vérifiez qu'il est branché et reconnu par "
    + "l'appareil.",
  NotReadableError:
    'Le micro est déjà utilisé par une autre application. Fermez la '
    + 'visioconférence ou l\'enregistreur qui le retient, puis réessayez.',
  TrackStartError:
    'Le micro est déjà utilisé par une autre application. Fermez la '
    + 'visioconférence ou l\'enregistreur qui le retient, puis réessayez.',
  OverconstrainedError:
    "Le micro trouvé ne convient pas à l'enregistrement. Choisissez-en un "
    + "autre dans les réglages de l'appareil.",
  SecurityError:
    "Le navigateur interdit l'accès au micro sur cette page. Vérifiez que "
    + "l'adresse commence bien par « https ».",
};

function formatRetenu(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  return FORMATS.find((f) => MediaRecorder.isTypeSupported(f)) || null;
}

function enDuree(secondes: number): string {
  const h = Math.floor(secondes / 3600);
  const m = Math.floor((secondes % 3600) / 60);
  const s = secondes % 60;
  const deux = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${deux(m)}:${deux(s)}` : `${m}:${deux(s)}`;
}

const EnregistreurReunion: React.FC<Props> = ({ onEnregistre, desactive }) => {
  const [etat, setEtat] = useState<'inactif' | 'enregistre' | 'pause'>('inactif');
  const [secondes, setSecondes] = useState(0);
  const [niveau, setNiveau] = useState(0);
  const [erreur, setErreur] = useState('');
  const [ouverture, setOuverture] = useState(false);
  const [ecoute, setEcoute] = useState('');

  const enregistreur = useRef<MediaRecorder | null>(null);
  const flux = useRef<MediaStream | null>(null);
  const morceaux = useRef<Blob[]>([]);
  const contexte = useRef<AudioContext | null>(null);
  const animation = useRef<number | null>(null);
  const minuteur = useRef<ReturnType<typeof setInterval> | null>(null);

  const libererEcoute = useRef('');

  const arreterMesure = useCallback(() => {
    if (animation.current !== null) cancelAnimationFrame(animation.current);
    animation.current = null;
    contexte.current?.close().catch(() => {});
    contexte.current = null;
    if (minuteur.current) clearInterval(minuteur.current);
    minuteur.current = null;
    setNiveau(0);
  }, []);

  /**
   * Rend le micro à l'appareil.
   *
   * Séparé de la mesure du niveau parce qu'il ne se fait pas au même moment :
   * couper le flux dès l'appui sur « Terminer » retirait la source à
   * l'enregistreur avant qu'il n'ait rendu son dernier morceau, et la capture
   * arrivait tronquée — parfois réduite à un en-tête que ffmpeg refuse comme
   * « données invalides ». On attend donc que l'enregistreur ait fini.
   */
  const libererMicro = useCallback(() => {
    flux.current?.getTracks().forEach((piste) => piste.stop());
    flux.current = null;
  }, []);

  useEffect(() => () => {
    arreterMesure();
    libererMicro();
    if (libererEcoute.current) URL.revokeObjectURL(libererEcoute.current);
  }, [arreterMesure, libererMicro]);

  // Fermer l'onglet pendant la captation perd la réunion : le navigateur ne
  // garde rien. On prévient tant qu'il est encore temps.
  useEffect(() => {
    if (etat === 'inactif') return undefined;
    const prevenir = (evenement: BeforeUnloadEvent) => {
      evenement.preventDefault();
      evenement.returnValue = '';
    };
    window.addEventListener('beforeunload', prevenir);
    return () => window.removeEventListener('beforeunload', prevenir);
  }, [etat]);

  const mesurerNiveau = (source: MediaStream) => {
    const Contexte = window.AudioContext || (window as any).webkitAudioContext;
    if (!Contexte) return;
    const audio = new Contexte();
    contexte.current = audio;
    // Un contexte ouvert en sommeil laisse la barre à zéro et l'écran affiche
    // « aucun son détecté » sur une captation pourtant bonne. On s'en assure
    // plutôt que de laisser croire à une panne.
    audio.resume().catch(() => {});
    const analyseur = audio.createAnalyser();
    analyseur.fftSize = 512;
    audio.createMediaStreamSource(source).connect(analyseur);

    const mesures = new Uint8Array(analyseur.frequencyBinCount);
    const lire = () => {
      analyseur.getByteTimeDomainData(mesures);
      let somme = 0;
      for (let i = 0; i < mesures.length; i += 1) {
        const ecart = (mesures[i] - 128) / 128;
        somme += ecart * ecart;
      }
      // Racine de la moyenne des carrés, étirée pour que la parole normale
      // occupe le milieu de la barre plutôt que son premier dixième.
      setNiveau(Math.min(1, Math.sqrt(somme / mesures.length) * 4));
      animation.current = requestAnimationFrame(lire);
    };
    lire();
  };

  const demarrer = async () => {
    setErreur('');
    const format = formatRetenu();
    if (!format) {
      setErreur(
        "Ce navigateur ne sait pas enregistrer. Enregistrez sur votre téléphone "
        + 'et déposez le fichier.',
      );
      return;
    }

    setOuverture(true);
    try {
      // Le traitement conçu pour les appels — suppression d'écho et de bruit —
      // rabote les voix lointaines, qui sont précisément celles du fond de
      // salle. On ne garde que la correction de niveau, qui les remonte.
      const source = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: true,
        },
      });
      flux.current = source;

      const machine = new MediaRecorder(source, {
        mimeType: format, audioBitsPerSecond: DEBIT,
      });
      morceaux.current = [];
      machine.ondataavailable = (evenement) => {
        if (evenement.data.size > 0) morceaux.current.push(evenement.data);
      };
      machine.onstop = () => {
        // Le dernier morceau est arrivé : le micro peut être rendu.
        libererMicro();
        const type = format.split(';')[0];
        const assemble = new Blob(morceaux.current, { type });

        // Une captation qui n'a rien retenu produit un fichier vide, que le
        // dépôt acceptait et qui n'échouait qu'au bout de la chaîne, sur un
        // message de ffmpeg en anglais. Mieux vaut le dire ici, tant que la
        // personne est encore devant son micro.
        if (assemble.size < TAILLE_MIN) {
          setErreur(
            "L'enregistrement est vide : rien n'a été capté. Vérifiez que le "
            + 'micro est autorisé et que la barre de niveau bouge, puis '
            + 'recommencez.',
          );
          setSecondes(0);
          return;
        }

        const horodatage = new Date();
        const nom = `reunion-${horodatage.toISOString().slice(0, 10)}`
          + `-${String(horodatage.getHours()).padStart(2, '0')}h`
          + `${String(horodatage.getMinutes()).padStart(2, '0')}`
          + `.${EXTENSIONS[type] || 'webm'}`;

        if (libererEcoute.current) URL.revokeObjectURL(libererEcoute.current);
        libererEcoute.current = URL.createObjectURL(assemble);
        setEcoute(libererEcoute.current);
        onEnregistre(new File([assemble], nom, { type }));
      };

      // Un morceau par seconde : si l'onglet tombe, ce qui a été remis est
      // déjà en mémoire plutôt que retenu dans l'encodeur.
      machine.start(1000);
      enregistreur.current = machine;

      setSecondes(0);
      setEcoute('');
      setEtat('enregistre');
      minuteur.current = setInterval(() => setSecondes((s) => s + 1), 1000);
      mesurerNiveau(source);
    } catch (e: any) {
      // Le micro a pu s'ouvrir avant que l'enregistreur ne refuse : sans cela
      // il resterait pris, et la tentative suivante échouerait à son tour.
      libererMicro();
      const nom = e?.name || '';
      setErreur(
        MOTIFS[nom]
          || "L'enregistrement n'a pas pu démarrer. Rechargez la page et "
            + `réessayez${nom ? ` (${nom})` : ''}.`,
      );
    } finally {
      setOuverture(false);
    }
  };

  const suspendre = () => {
    if (!enregistreur.current) return;
    if (etat === 'enregistre') {
      enregistreur.current.pause();
      if (minuteur.current) clearInterval(minuteur.current);
      minuteur.current = null;
      setEtat('pause');
    } else {
      enregistreur.current.resume();
      minuteur.current = setInterval(() => setSecondes((s) => s + 1), 1000);
      setEtat('enregistre');
    }
  };

  const arreter = () => {
    const machine = enregistreur.current;
    enregistreur.current = null;
    arreterMesure();
    setEtat('inactif');
    // Le micro est rendu par « onstop », une fois le dernier morceau remis.
    // Sans enregistreur à attendre, on le rend tout de suite.
    if (machine) machine.stop(); else libererMicro();
  };

  if (etat === 'inactif' && !ecoute) {
    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={demarrer}
          disabled={desactive || ouverture}
          className="inline-flex items-center gap-2 rounded-md border border-[#DDE2DD]
            bg-white px-4 py-3 text-sm font-medium text-eg-encre
            hover:border-eg-accent disabled:opacity-50"
        >
          {ouverture
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Mic className="w-4 h-4 text-eg-accent" />}
          Enregistrer la réunion maintenant
        </button>
        <p className="text-xs text-eg-estompe">
          Posez l'appareil au centre de la table. Gardez cet onglet ouvert
          pendant toute la séance : fermer la page perdrait l'enregistrement.
        </p>
        {erreur && (
          <div className="flex items-start gap-2 rounded-md border border-[#E4B9B9]
            bg-[#FBF1F1] px-3 py-2 text-sm text-[#8A2A2A]">
            <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
            {erreur}
          </div>
        )}
      </div>
    );
  }

  if (etat === 'inactif' && ecoute) {
    return (
      <div className="space-y-2 rounded-md border border-[#DDE2DD] bg-eg-voile px-3 py-3">
        <p className="text-sm text-eg-encre">
          Enregistrement de {enDuree(secondes)} prêt à être déposé. Réécoutez-le
          avant de l'envoyer : une salle mal captée se transcrit mal.
        </p>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio controls src={ecoute} className="w-full" />
        <button
          type="button"
          onClick={() => { setEcoute(''); setSecondes(0); }}
          className="text-xs text-eg-accent hover:underline"
        >
          Recommencer l'enregistrement
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-md border border-[#DDE2DD] bg-white px-3 py-3">
      <div className="flex items-center gap-3">
        <Circle
          className={`w-3 h-3 shrink-0 fill-current ${
            etat === 'enregistre' ? 'animate-pulse text-[#B3261E]' : 'text-eg-estompe'
          }`}
        />
        <span className="font-mono text-lg text-eg-encre">{enDuree(secondes)}</span>
        <span className="text-sm text-eg-gris">
          {etat === 'enregistre' ? 'Captation en cours' : 'En pause'}
        </span>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={suspendre}
            className="inline-flex items-center gap-1 rounded-md border border-[#DDE2DD]
              px-3 py-1.5 text-sm text-eg-encre"
          >
            {etat === 'enregistre'
              ? <><Pause className="w-4 h-4" /> Pause</>
              : <><Play className="w-4 h-4" /> Reprendre</>}
          </button>
          <button
            type="button"
            onClick={arreter}
            className="inline-flex items-center gap-1 rounded-md bg-eg-ombre px-3 py-1.5
              text-sm font-medium text-white"
          >
            <Square className="w-4 h-4" /> Terminer
          </button>
        </div>
      </div>

      <div>
        <div className="h-2 w-full overflow-hidden rounded bg-eg-voile">
          <div
            className={`h-full transition-[width] duration-75 ${
              niveau > 0.02 ? 'bg-eg-accent' : 'bg-[#E4B9B9]'
            }`}
            style={{ width: `${Math.round(niveau * 100)}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-eg-estompe">
          {etat === 'pause'
            ? 'Captation suspendue.'
            : niveau > 0.02
              ? 'Le micro capte la salle.'
              : 'Aucun son détecté — vérifiez le micro avant de poursuivre.'}
        </p>
      </div>
    </div>
  );
};

export default EnregistreurReunion;
